import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import React from 'react';
import { CommunityBudgetCurrencySupportDto } from '@api/ChallengeClient';

interface Props {
    budget: CommunityBudgetCurrencySupportDto | null;
    onClose(): void;
    onDelete(currencyCode: string, comment: string): Promise<void>;
}

export const DeleteBudgetDialog = ({ budget, onClose, onDelete }: Props) => {
    const [comment, setComment] = React.useState('');
    const [deleting, setDeleting] = React.useState(false);
    const [error, setError] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        if (budget) {
            setComment('');
            setError(undefined);
        }
    }, [budget]);

    if (!budget) {
        return null;
    }

    const hasUsage = Number(budget.currentPeriod?.currentUsage ?? 0) !== 0;

    const submit = async () => {
        setDeleting(true);
        setError(undefined);
        try {
            await onDelete(budget.currencyCode, comment.trim());
            onClose();
        } catch (e) {
            setError('Could not remove the budget. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog open onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        Remove {budget.currencyCode.split('_')[0]} budget
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <p className="text-sm">
                        This community will no longer be tracked for this
                        currency. New rewards in it stop being recorded.
                    </p>
                    <span className="text-xs break-all text-muted-foreground">
                        {budget.currencyCode}
                    </span>
                    {hasUsage && (
                        <Alert variant="warning">
                            <AlertDescription>
                                This budget has recorded usage. Removing it also
                                hides that usage history from the ledger and
                                every total. This cannot be undone from the
                                portal.
                            </AlertDescription>
                        </Alert>
                    )}
                    <TextField
                        label="Reason"
                        type="text"
                        required
                        multiline
                        maxRows={2}
                        value={comment}
                        onChange={(next) => setComment(String(next))}
                    />
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        disabled={comment.trim().length === 0}
                        loading={deleting}
                        onClick={submit}
                    >
                        Remove budget
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
